import type { GameState, TilePosition } from '../../model/gamestate/GameState';
import {
    ENTITY_FADE_IN_DURATION_MS,
    ENTITY_FADE_OUT_DURATION_MS,
    ENTITY_MOVE_SPEED_TILES_PER_SECOND,
    REMOTE_TELEPORT_SNAP_DISTANCE,
} from './constants';
import type { ActorVisual, InterpolatedActorData, InterpolatedEntityState, PixelPosition, VisualEffect } from './types';

export class EntityManager {
    private actorVisuals = new Map<string, InterpolatedEntityState<InterpolatedActorData>>();
    private pendingActorSnaps = new Map<string, TilePosition>();
    private activeEffects: VisualEffect[] = [];

    constructor() {}

    public getActorVisuals(): Map<string, InterpolatedEntityState<InterpolatedActorData>> {
        return this.actorVisuals;
    }

    public getSelfActorState(gameState: GameState): InterpolatedEntityState<InterpolatedActorData> | null {
        const selfActorId = gameState.getSelfEntityId() ?? 'self:local';
        return this.actorVisuals.get(selfActorId) ?? null;
    }

    public markActorSnap(entityId: string, target: TilePosition): void {
        if (!entityId) {
            return;
        }
        this.pendingActorSnaps.set(entityId, { ...target });
    }

    public addEffect(effect: VisualEffect): void {
        this.activeEffects.push(effect);
    }

    public getActiveEffects(): VisualEffect[] {
        return this.activeEffects;
    }

    public updateEffects(deltaSeconds: number): boolean {
        if (this.activeEffects.length === 0) return false;

        this.activeEffects = this.activeEffects.filter((effect) => {
            effect.update(deltaSeconds);
            const keep = !effect.isComplete();
            if (!keep) {
                effect.disposeScene?.();
            }
            return keep;
        });

        return this.activeEffects.length > 0;
    }

    public renderEffectsOverlay(ctx: CanvasRenderingContext2D): void {
        for (const effect of this.activeEffects) {
            if (!effect.renderOverlay) {
                continue;
            }
            ctx.save();
            effect.renderOverlay(ctx);
            ctx.restore();
        }
    }

    public clear(): void {
        this.actorVisuals.clear();
        this.pendingActorSnaps.clear();
        this.clearEffects();
    }

    public clearEffects(): void {
        for (const effect of this.activeEffects) {
            effect.disposeScene?.();
        }
        this.activeEffects = [];
    }

    public reprojectActorWorldCenters(resolveWorldCenter: (position: TilePosition) => PixelPosition): void {
        for (const state of this.actorVisuals.values()) {
            state.currentCenter = resolveWorldCenter(state.current);
            state.targetCenter = resolveWorldCenter(state.target);
            this.recalculateWorldMoveSpeed(state);
        }
    }

    public updateActorVisuals(
        deltaSeconds: number,
        gameState: GameState,
        lastCharacterPosition: TilePosition | null,
        selfSnapTarget: TilePosition | null,
        localCharacterRotation: number,
        playerDisplayName: string,
        resolveWorldCenter: (position: TilePosition) => PixelPosition
    ): { hasActiveAnimation: boolean; clearedSnapTarget: boolean } {
        const nowMs = performance.now();
        let hasActiveAnimation = false;
        let clearedSnapTarget = false;

        const selfActorId = gameState.getSelfEntityId() ?? 'self:local';

        for (const state of this.actorVisuals.values()) {
            state.isInServerSet = false;
        }

        const selfPosition = lastCharacterPosition ?? gameState.getCharacterPosition();
        const selfAppearance = gameState.getSelfAppearance();

        const sources: InterpolatedActorData[] = [
            {
                id: selfActorId,
                kind: 'self',
                entityType: selfAppearance.entityType,
                color: selfAppearance.color,
                accessoriesJson: selfAppearance.accessoriesJson,
                position: selfPosition,
                rotation: localCharacterRotation,
                displayName: playerDisplayName || undefined,
                forceSnap:
                    selfSnapTarget?.x === selfPosition.x &&
                    selfSnapTarget?.y === selfPosition.y,
            },
            ...gameState.getRemotePlayers().map((player: any) => ({
                id: player.entityId,
                playerId: player.playerId,
                kind: 'remotePlayer' as const,
                entityType: player.entityType,
                color: player.color,
                accessoriesJson: player.accessoriesJson,
                position: player.position,
                prevMapX: player.prevMapX,
                prevMapY: player.prevMapY,
                rotation: player.rotation,
                displayName: player.displayName,
                hp: player.hp,
                maxHp: player.maxHp,
                stamina: player.stamina,
                maxStamina: player.maxStamina,
                isEntering: player.isEntering,
                isExiting: player.isExiting,
                forceSnap: player.forceSnap,
            })),
            ...gameState.getRuntimeMobs().map((mob: any) => ({
                id: mob.entityId,
                kind: 'mob' as const,
                entityType: mob.entityType,
                color: mob.color,
                accessoriesJson: mob.accessoriesJson,
                position: mob.position,
                prevMapX: mob.prevMapX,
                prevMapY: mob.prevMapY,
                rotation: mob.rotation,
                hp: mob.hp,
                maxHp: mob.maxHp,
                stamina: mob.stamina,
                maxStamina: mob.maxStamina,
                mobInventoryValue: mob.mobInventoryValue,
                mobInventoryItemsJson: mob.mobInventoryItemsJson,
                isEntering: mob.isEntering,
                isExiting: mob.isExiting,
                forceSnap: mob.forceSnap,
            })),
        ];

        for (const actor of sources) {
            const existing = this.actorVisuals.get(actor.id);
            const resolvedRotation =
                typeof actor.rotation === 'number'
                    ? actor.rotation
                    : existing?.data.rotation;
            const actorWithRotation: InterpolatedActorData = {
                ...actor,
                rotation: resolvedRotation,
            };

            const state = this.getOrCreateInterpolatedState(
                actorWithRotation.id,
                actorWithRotation.position,
                actorWithRotation,
                nowMs,
                selfActorId,
                resolveWorldCenter
            );
            state.isInServerSet = true;
            state.lastServerSeenAtMs = nowMs;

            let shouldForceSnap = actorWithRotation.forceSnap === true;
            const pendingSnap = this.pendingActorSnaps.get(actorWithRotation.id);
            if (pendingSnap) {
                this.pendingActorSnaps.delete(actorWithRotation.id);
                if (
                    pendingSnap.x === actorWithRotation.position.x &&
                    pendingSnap.y === actorWithRotation.position.y
                ) {
                    shouldForceSnap = true;
                } else {
                    // Rebase from snap location, then animate toward latest authoritative position.
                    state.current = { ...pendingSnap };
                    state.currentCenter = resolveWorldCenter(pendingSnap);
                }
            }

            const isExiting = (actorWithRotation as any).isExiting;
            if (isExiting) {
                if (state.fadeState !== 'fadingOut') {
                    this.beginOpacityFade(
                        state,
                        0,
                        nowMs,
                        this.estimateFadeOutDurationMs(state),
                        'fadingOut'
                    );
                }
            } else {
                // Normal visibility or entering
                if (state.fadeState === 'fadingOut' || state.opacity < 1) {
                    if (state.fadeState !== 'fadingIn') {
                        this.beginOpacityFade(state, 1, nowMs, ENTITY_FADE_IN_DURATION_MS, 'fadingIn');
                    }
                }
            }

            this.updateInterpolatedStateTarget(
                state,
                actorWithRotation.position,
                shouldForceSnap,
                resolveWorldCenter
            );

            if (actorWithRotation.id === selfActorId && shouldForceSnap) {
                clearedSnapTarget = true;
            }
        }

        for (const [id, state] of this.actorVisuals.entries()) {
            if (!state.isInServerSet) {
                if (state.fadeState !== 'fadingOut') {
                    this.beginOpacityFade(
                        state,
                        0,
                        nowMs,
                        this.estimateFadeOutDurationMs(state),
                        'fadingOut'
                    );
                }

                if (state.opacity <= 0 && state.fadeState === 'fadingOut') {
                    this.actorVisuals.delete(id);
                    continue;
                }
            }

            const animating = this.advanceInterpolatedState(state, deltaSeconds, nowMs);
            if (animating) hasActiveAnimation = true;
        }

        return { hasActiveAnimation, clearedSnapTarget };
    }

    private getOrCreateInterpolatedState(
        id: string,
        position: TilePosition,
        data: InterpolatedActorData,
        nowMs: number,
        selfActorId: string,
        resolveWorldCenter: (position: TilePosition) => PixelPosition
    ): InterpolatedEntityState<InterpolatedActorData> {
        const existing = this.actorVisuals.get(id);
        if (existing) {
            existing.data = data;
            return existing;
        }

        // Use previous position provided by server for smooth entry (fix pop-in)
        const startX = (data as any).prevMapX ?? position.x;
        const startY = (data as any).prevMapY ?? position.y;

        const startPosition = { x: startX, y: startY };
        const created: InterpolatedEntityState<InterpolatedActorData> = {
            current: startPosition,
            target: { ...position },
            currentCenter: resolveWorldCenter(startPosition),
            targetCenter: resolveWorldCenter(position),
            worldMoveSpeedPerSecond: 0,
            opacity: 0,
            fadeState: 'fadingIn',
            fadeFromOpacity: 0,
            fadeToOpacity: 1,
            fadeStartedAtMs: nowMs,
            fadeDurationMs: ENTITY_FADE_IN_DURATION_MS,
            lastServerSeenAtMs: nowMs,
            isInServerSet: true,
            data
        };

        if (id === selfActorId) {
            created.opacity = 1;
            created.fadeState = 'visible';
            created.fadeFromOpacity = 1;
            created.fadeToOpacity = 1;
            created.fadeDurationMs = 1;
        }

        // Only snap if we don't have a valid previous position AND it's too far
        if (typeof (data as any).prevMapX !== 'number') {
            const dist = Math.sqrt(
                Math.pow(created.current.x - created.target.x, 2) +
                Math.pow(created.current.y - created.target.y, 2)
            );

            if (dist > REMOTE_TELEPORT_SNAP_DISTANCE) {
                created.current = { ...created.target };
                created.currentCenter = { ...created.targetCenter };
            }
        }

        this.recalculateWorldMoveSpeed(created);
        this.actorVisuals.set(id, created);
        return created;
    }

    private updateInterpolatedStateTarget(
        state: InterpolatedEntityState,
        position: TilePosition,
        forceSnap: boolean,
        resolveWorldCenter: (position: TilePosition) => PixelPosition
    ): void {
        state.targetCenter = resolveWorldCenter(position);
        if (forceSnap) {
            state.current = { ...position };
            state.target = { ...position };
            state.currentCenter = { ...state.targetCenter };
            state.worldMoveSpeedPerSecond = 0;
            return;
        }

        state.target = { ...position };
        this.recalculateWorldMoveSpeed(state);
    }

    private advanceInterpolatedState(
        state: InterpolatedEntityState,
        deltaSeconds: number,
        nowMs: number
    ): boolean {
        state.current = this.moveTowardPosition(
            state.current,
            state.target,
            ENTITY_MOVE_SPEED_TILES_PER_SECOND * deltaSeconds
        );
        state.currentCenter = this.moveTowardPosition(
            state.currentCenter,
            state.targetCenter,
            state.worldMoveSpeedPerSecond * deltaSeconds
        );

        this.advanceOpacityFade(state, nowMs);

        const positionAnimating = !this.isCloseToTarget(state.currentCenter, state.targetCenter);
        const opacityAnimating = state.fadeState !== 'visible';

        return positionAnimating || opacityAnimating;
    }

    private moveTowardPosition(from: { x: number; y: number }, to: { x: number; y: number }, speed: number): { x: number; y: number } {
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance <= speed) {
            return { x: to.x, y: to.y };
        }

        const ratio = speed / distance;
        return {
            x: from.x + dx * ratio,
            y: from.y + dy * ratio
        };
    }

    private isCloseToTarget(current: { x: number; y: number }, target: { x: number; y: number }): boolean {
        const threshold = 0.05; // Within 5% of a tile
        const dx = Math.abs(current.x - target.x);
        const dy = Math.abs(current.y - target.y);
        return dx < threshold && dy < threshold;
    }

    private estimateFadeOutDurationMs(state: InterpolatedEntityState): number {
        const dx = state.targetCenter.x - state.currentCenter.x;
        const dy = state.targetCenter.y - state.currentCenter.y;
        const distWorld = Math.sqrt(dx * dx + dy * dy);
        if (distWorld <= Number.EPSILON || state.worldMoveSpeedPerSecond <= Number.EPSILON) {
            return ENTITY_FADE_OUT_DURATION_MS;
        }
        const moveMs = (distWorld / state.worldMoveSpeedPerSecond) * 1000;
        return Math.max(120, Math.min(ENTITY_FADE_OUT_DURATION_MS, moveMs));
    }

    private recalculateWorldMoveSpeed(state: InterpolatedEntityState): void {
        const tileDx = state.target.x - state.current.x;
        const tileDy = state.target.y - state.current.y;
        const tileDistance = Math.sqrt(tileDx * tileDx + tileDy * tileDy);
        const worldDx = state.targetCenter.x - state.currentCenter.x;
        const worldDy = state.targetCenter.y - state.currentCenter.y;
        const worldDistance = Math.sqrt(worldDx * worldDx + worldDy * worldDy);
        if (tileDistance <= Number.EPSILON || worldDistance <= Number.EPSILON) {
            state.worldMoveSpeedPerSecond = 0;
            return;
        }
        const durationSeconds = tileDistance / ENTITY_MOVE_SPEED_TILES_PER_SECOND;
        state.worldMoveSpeedPerSecond = durationSeconds > 0 ? worldDistance / durationSeconds : 0;
    }

    private beginOpacityFade(
        state: InterpolatedEntityState,
        targetOpacity: number,
        nowMs: number,
        durationMs: number,
        fadeState: 'fadingIn' | 'fadingOut'
    ): void {
        if (state.fadeState === fadeState && state.fadeToOpacity === targetOpacity) {
            return;
        }

        state.fadeState = fadeState;
        state.fadeFromOpacity = state.opacity;
        state.fadeToOpacity = targetOpacity;
        state.fadeStartedAtMs = nowMs;
        state.fadeDurationMs = Math.max(1, durationMs);
    }

    private advanceOpacityFade(state: InterpolatedEntityState, nowMs: number): void {
        if (state.fadeState === 'visible') {
            return;
        }

        const elapsedMs = nowMs - state.fadeStartedAtMs;
        const progress = Math.max(0, Math.min(1, elapsedMs / state.fadeDurationMs));
        state.opacity = state.fadeFromOpacity + (state.fadeToOpacity - state.fadeFromOpacity) * progress;

        if (progress >= 1) {
            state.opacity = state.fadeToOpacity;
            if (state.fadeToOpacity >= 1) {
                state.fadeState = 'visible';
            }
        }
    }
}
